/**
 * Unauthenticated pages: sign in, sign up, verify email, forgot/reset password, accept
 * invitation, confirm email; plus first-run portfolio set-up.
 */
import {
  acceptInviteSchema,
  forgotPasswordSchema,
  loginSchema,
  onboardingSchema,
  resetPasswordSchema,
  signupSchema,
  verifyEmailSchema,
} from "@shared/schemas/auth.ts";
import type { MeResponse } from "@shared/types.ts";
import { type ReactNode, useEffect, useRef, useState } from "react";
import { Alert, Button, Card } from "react-bootstrap";
import { FormProvider } from "react-hook-form";
import { Link, Navigate, useLocation, useNavigate, useSearchParams } from "react-router";
import { type ChallengeInfo, SecondFactorForm } from "../../components/auth/SecondFactor.tsx";
import { Turnstile } from "../../components/auth/Turnstile.tsx";
import { CheckboxField, FormError, SelectField, TextField } from "../../components/forms/fields.tsx";
import { Loading, SubmitButton } from "../../components/ui/kit.tsx";
import { ApiError, api } from "../../lib/api.ts";
import { useAuth } from "../../lib/auth.tsx";
import { useSchemaForm } from "../../lib/forms.ts";
import { useApi } from "../../lib/query.ts";

function AuthCard({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="auth-page">
      <Card className="auth-card shadow-lg">
        <Card.Body className="p-4 p-md-5">
          <div className="d-flex align-items-center gap-2 mb-4">
            <img src="/favicon.svg" alt="" width={32} height={32} />
            <span className="fw-bold">Property Manager</span>
          </div>
          <h1 className="h4 mb-1">{title}</h1>
          {subtitle && <p className="text-muted">{subtitle}</p>}
          {children}
        </Card.Body>
      </Card>
    </div>
  );
}

interface AuthConfig {
  signupEnabled: boolean;
  turnstileSiteKey: string | null;
  termsUrl: string | null;
  privacyUrl: string | null;
}

function useAuthConfig(): AuthConfig | undefined {
  return useApi<AuthConfig>("/api/auth/config").data;
}

/** The Turnstile widget wired into a form's `turnstileToken` field, reset after each attempt. */
function useBotCheck(
  form: { setValue: (name: "turnstileToken", value: string) => void },
  action: "signup" | "login" | "password_reset",
) {
  const cfg = useAuthConfig();
  const [resetKey, setResetKey] = useState(0);
  const widget = cfg?.turnstileSiteKey ? (
    <Turnstile
      siteKey={cfg.turnstileSiteKey}
      action={action}
      resetKey={resetKey}
      onToken={(t) => form.setValue("turnstileToken", t)}
    />
  ) : null;
  return { cfg, widget, reset: () => setResetKey((k) => k + 1) };
}

export function LoginPage() {
  const { me, setMe } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { form, formError, submit } = useSchemaForm(loginSchema, {
    email: "",
    password: "",
    turnstileToken: "",
  });
  const bot = useBotCheck(form, "login");
  const [mfa, setMfa] = useState<(ChallengeInfo & { mfaToken: string }) | null>(null);
  const from = (location.state as { from?: string } | null)?.from ?? "/";
  if (me) return <Navigate to={from} replace />;
  if (mfa)
    return (
      <AuthCard title="Two-step verification" subtitle="Confirm it's you with your second factor.">
        <SecondFactorForm
          info={mfa}
          submitLabel="Sign in"
          onSubmit={async (factor) => {
            setMe(await api.post<MeResponse>("/api/auth/login/mfa", { mfaToken: mfa.mfaToken, ...factor }));
            navigate(from, { replace: true });
          }}
        />
        <div className="text-center small mt-2">
          <Button variant="link" size="sm" onClick={() => setMfa(null)}>
            Start again
          </Button>
        </div>
      </AuthCard>
    );
  return (
    <AuthCard title="Sign in" subtitle="Manage your properties, finances and tax records.">
      <FormProvider {...form}>
        <form
          noValidate
          onSubmit={submit(async (value) => {
            try {
              const next = await api.post<
                MeResponse | (ChallengeInfo & { mfaRequired: true; mfaToken: string })
              >("/api/auth/login", value);
              if ("mfaRequired" in next) {
                setMfa(next);
                return;
              }
              setMe(next);
              navigate(from, { replace: true });
            } finally {
              bot.reset();
            }
          })}
        >
          <FormError message={formError} />
          <TextField
            name="email"
            label="Email address"
            type="email"
            autoComplete="username"
            autoFocus
            required
          />
          <TextField
            name="password"
            label="Password"
            type="password"
            autoComplete="current-password"
            required
          />
          {bot.widget}
          <div className="d-grid mb-3">
            <SubmitButton busy={form.formState.isSubmitting}>Sign in</SubmitButton>
          </div>
          <div className="text-center small">
            <Link to="/forgot-password">Forgot your password?</Link>
            {bot.cfg?.signupEnabled && (
              <>
                {" · "}
                <Link to="/signup">Create an account</Link>
              </>
            )}
          </div>
        </form>
      </FormProvider>
    </AuthCard>
  );
}

export function ForgotPasswordPage() {
  const [sent, setSent] = useState(false);
  const { form, formError, submit } = useSchemaForm(forgotPasswordSchema, { email: "", turnstileToken: "" });
  const bot = useBotCheck(form, "password_reset");
  return (
    <AuthCard title="Reset your password" subtitle="We'll email you a link to choose a new password.">
      {sent ? (
        <Alert variant="success">
          If an account exists for that address, a reset link is on its way. The link expires in one hour.
        </Alert>
      ) : (
        <FormProvider {...form}>
          <form
            noValidate
            onSubmit={submit(async (value) => {
              try {
                await api.post("/api/auth/forgot-password", value);
                setSent(true);
              } finally {
                bot.reset();
              }
            })}
          >
            <FormError message={formError} />
            <TextField
              name="email"
              label="Email address"
              type="email"
              autoComplete="username"
              autoFocus
              required
            />
            {bot.widget}
            <div className="d-grid">
              <SubmitButton busy={form.formState.isSubmitting}>Send reset link</SubmitButton>
            </div>
          </form>
        </FormProvider>
      )}
      <div className="text-center mt-3">
        <Link to="/login" className="small">
          Back to sign in
        </Link>
      </div>
    </AuthCard>
  );
}

export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const [done, setDone] = useState(false);
  const { form, formError, submit } = useSchemaForm(resetPasswordSchema, {
    token: params.get("token") ?? "",
    password: "",
  });
  return (
    <AuthCard title="Choose a new password">
      {done ? (
        <Alert variant="success">
          Your password has been changed. <Link to="/login">Sign in</Link>
        </Alert>
      ) : (
        <FormProvider {...form}>
          <form
            noValidate
            onSubmit={submit(async (value) => {
              await api.post("/api/auth/reset-password", value);
              setDone(true);
            })}
          >
            <FormError message={formError} />
            <TextField
              name="password"
              label="New password"
              type="password"
              autoComplete="new-password"
              hint="At least 10 characters."
              required
              autoFocus
            />
            <div className="d-grid">
              <SubmitButton busy={form.formState.isSubmitting}>Save password</SubmitButton>
            </div>
          </form>
        </FormProvider>
      )}
    </AuthCard>
  );
}

export function AcceptInvitePage() {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const { setMe } = useAuth();
  const navigate = useNavigate();
  const invite = useApi<{ email: string; teamName: string; accountExists: boolean }>(
    token ? `/api/invitations/${encodeURIComponent(token)}` : null,
  );
  const { form, formError, submit } = useSchemaForm(acceptInviteSchema, { token, name: "", password: "" });
  if (invite.isLoading)
    return (
      <AuthCard title="Invitation">
        <Loading />
      </AuthCard>
    );
  if (!invite.data)
    return (
      <AuthCard title="Invitation not found">
        <Alert variant="warning">This invitation is invalid, has expired or has already been used.</Alert>
        <Link to="/login">Go to sign in</Link>
      </AuthCard>
    );
  return (
    <AuthCard
      title={`Join ${invite.data.teamName}`}
      subtitle={`You've been invited as ${invite.data.email}.`}
    >
      <FormProvider {...form}>
        <form
          noValidate
          onSubmit={submit(async (value) => {
            const next = await api.post<MeResponse | { mfaRequired: true }>("/api/invitations/accept", value);
            // Accounts with two-step verification finish by signing in normally.
            if ("mfaRequired" in next) {
              navigate("/login", { replace: true });
              return;
            }
            setMe(next);
            navigate("/", { replace: true });
          })}
        >
          <FormError message={formError} />
          {!invite.data.accountExists && (
            <TextField name="name" label="Your name" autoComplete="name" required />
          )}
          <TextField
            name="password"
            label={invite.data.accountExists ? "Your existing password" : "Choose a password"}
            type="password"
            autoComplete={invite.data.accountExists ? "current-password" : "new-password"}
            hint={
              invite.data.accountExists
                ? "You already have an account — confirm it's you."
                : "At least 10 characters."
            }
            required
          />
          <div className="d-grid">
            <SubmitButton busy={form.formState.isSubmitting}>Accept invitation</SubmitButton>
          </div>
        </form>
      </FormProvider>
    </AuthCard>
  );
}

export function ConfirmEmailPage() {
  const [params] = useSearchParams();
  const [state, setState] = useState<"working" | "done" | "failed">("working");
  const { refresh } = useAuth();
  const started = useRef(false);
  useEffect(() => {
    // Tokens are single-use: never submit twice (e.g. StrictMode double effects).
    if (started.current) return;
    started.current = true;
    api
      .post("/api/auth/confirm-email", { token: params.get("token") ?? "" })
      .then(async () => {
        setState("done");
        await refresh();
      })
      .catch(() => setState("failed"));
  }, [params, refresh]);
  return (
    <AuthCard title="Confirm email address">
      {state === "working" && <Loading label="Confirming…" />}
      {state === "done" && <Alert variant="success">Your email address has been updated.</Alert>}
      {state === "failed" && <Alert variant="danger">This link is invalid or has expired.</Alert>}
      <Link to="/">Continue</Link>
    </AuthCard>
  );
}

export function SignupPage() {
  const [sentTo, setSentTo] = useState<string | null>(null);
  const { form, formError, submit } = useSchemaForm(signupSchema, {
    name: "",
    email: "",
    password: "",
    acceptTerms: false,
    turnstileToken: "",
  });
  const bot = useBotCheck(form, "signup");
  const { me } = useAuth();
  if (me) return <Navigate to="/" replace />;
  if (bot.cfg && !bot.cfg.signupEnabled)
    return (
      <AuthCard title="Sign-up is closed">
        <p>New accounts are by invitation only. Ask the owner of your portfolio to invite you.</p>
        <Link to="/login">Sign in</Link>
      </AuthCard>
    );
  if (sentTo)
    return (
      <AuthCard title="Check your email">
        <Alert variant="success">
          We've sent a link to <strong>{sentTo}</strong>. Open it to confirm your address and finish setting
          up. If it hasn't arrived in a few minutes, check your spam folder.
        </Alert>
        <Link to="/login">Back to sign in</Link>
      </AuthCard>
    );
  const terms = bot.cfg?.termsUrl;
  const privacy = bot.cfg?.privacyUrl;
  return (
    <AuthCard
      title="Create your account"
      subtitle="Keep your rental records in one place and see what to enter on your tax return."
    >
      <FormProvider {...form}>
        <form
          noValidate
          onSubmit={submit(async (value) => {
            try {
              await api.post("/api/auth/signup", value);
              setSentTo(value.email);
            } finally {
              bot.reset();
            }
          })}
        >
          <FormError message={formError} />
          <TextField name="name" label="Your name" autoComplete="name" autoFocus required />
          <TextField name="email" label="Email address" type="email" autoComplete="email" required />
          <TextField
            name="password"
            label="Choose a password"
            type="password"
            autoComplete="new-password"
            hint="At least 10 characters."
            required
          />
          <CheckboxField
            name="acceptTerms"
            label={
              <>
                I agree to the{" "}
                {terms ? (
                  <a href={terms} target="_blank" rel="noreferrer">
                    terms of service
                  </a>
                ) : (
                  "terms of service"
                )}{" "}
                and{" "}
                {privacy ? (
                  <a href={privacy} target="_blank" rel="noreferrer">
                    privacy policy
                  </a>
                ) : (
                  "privacy policy"
                )}
              </>
            }
          />
          {bot.widget}
          <div className="d-grid mb-3">
            <SubmitButton busy={form.formState.isSubmitting}>Create account</SubmitButton>
          </div>
          <div className="text-center small">
            Already have an account? <Link to="/login">Sign in</Link>
          </div>
        </form>
      </FormProvider>
    </AuthCard>
  );
}

export function VerifyEmailPage() {
  const [params] = useSearchParams();
  const { setMe } = useAuth();
  const navigate = useNavigate();
  const { form, formError, submit } = useSchemaForm(verifyEmailSchema, {
    token: params.get("token") ?? "",
    password: "",
  });
  return (
    <AuthCard title="Confirm your email address">
      <FormProvider {...form}>
        <form
          noValidate
          onSubmit={submit(async (value) => {
            const next = await api.post<MeResponse>("/api/auth/verify-email", value);
            setMe(next);
            navigate(next.teams.length === 0 ? "/welcome" : "/", { replace: true });
          })}
        >
          <FormError message={formError} />
          <TextField
            name="password"
            label="Password"
            type="password"
            autoComplete="current-password"
            hint="The password you chose when you signed up."
            required
            autoFocus
          />
          <div className="d-grid">
            <SubmitButton busy={form.formState.isSubmitting}>Confirm and sign in</SubmitButton>
          </div>
          <div className="text-center mt-3 small">
            <Link to="/forgot-password">Forgotten your password?</Link>
          </div>
        </form>
      </FormProvider>
    </AuthCard>
  );
}

/** Target of the unsubscribe link in reminder and digest emails (#49). Confirms before acting. */
export function UnsubscribePage() {
  const [params] = useSearchParams();
  const [state, setState] = useState<"idle" | "busy" | "done">("idle");
  const [error, setError] = useState<string | null>(null);
  const token = params.get("token") ?? "";
  return (
    <AuthCard title="Unsubscribe from reminder emails">
      {state === "done" ? (
        <Alert variant="success">
          You won't get reminder or digest emails any more. You'll still see reminders in the app, and you can
          turn emails back on under Settings → Notifications.
        </Alert>
      ) : (
        <>
          {error && <Alert variant="danger">{error}</Alert>}
          <p>Stop reminder and digest emails? Account emails, such as password resets, are still sent.</p>
          <Button
            disabled={!token || state === "busy"}
            onClick={async () => {
              setState("busy");
              setError(null);
              try {
                await api.post(`/api/email/unsubscribe?token=${encodeURIComponent(token)}`, {});
                setState("done");
              } catch (e) {
                setError(e instanceof ApiError ? e.message : "Something went wrong");
                setState("idle");
              }
            }}
          >
            Unsubscribe
          </Button>
        </>
      )}
    </AuthCard>
  );
}

/** First-run set-up for an individual landlord: their portfolio and themselves as its owner. */
export function WelcomePage() {
  const { me, setMe } = useAuth();
  const navigate = useNavigate();
  const { form, formError, submit } = useSchemaForm(onboardingSchema, {
    teamName: me ? `${me.user.name}'s portfolio` : "",
    ownerName: me?.user.name ?? "",
    accountingBasis: "cash",
  });
  if (me && me.teams.length > 0) return <Navigate to="/" replace />;
  return (
    <AuthCard title="Set up your portfolio" subtitle="Two quick questions and you're in.">
      <FormProvider {...form}>
        <form
          noValidate
          onSubmit={submit(async (value) => {
            setMe(await api.post<MeResponse>("/api/onboarding", value));
            navigate("/", { replace: true });
          })}
        >
          <FormError message={formError} />
          <TextField
            name="teamName"
            label="Portfolio name"
            hint="Just for you. You can change it later in Settings."
            required
          />
          <TextField
            name="ownerName"
            label="Your full legal name"
            hint="As it appears on your tax return. You're added as the owner of your properties."
            required
          />
          <SelectField
            name="accountingBasis"
            label="How do you work out your rental profits?"
            options={[
              { value: "cash", label: "Cash basis: money in and out when it's paid (most landlords)" },
              {
                value: "accruals",
                label: "Traditional accounting (accruals): income and costs for the period they cover",
              },
            ]}
            hint="If you're not sure, choose the cash basis. It's the default for landlords with income up to £150,000."
          />
          <div className="d-grid">
            <SubmitButton busy={form.formState.isSubmitting}>Start using the app</SubmitButton>
          </div>
        </form>
      </FormProvider>
    </AuthCard>
  );
}

/**
 * Route guard: renders children when signed in, otherwise redirects to /login. Signed-in
 * users without a portfolio go to set-up first (staff go to the console).
 */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { me, loading } = useAuth();
  const location = useLocation();
  if (loading) return <Loading />;
  if (!me) return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  if (me.teams.length === 0 && location.pathname !== "/welcome" && !location.pathname.startsWith("/staff"))
    // Staff accounts (#52) have no portfolio of their own: they work from the console.
    return <Navigate to={me.isStaff ? "/staff" : "/welcome"} replace />;
  return <>{children}</>;
}
